import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  QrCode, Link2, Download, Copy, Check, Image as ImageIcon,
  Trash2, Palette, Type, AlertCircle, Wifi, Eye, EyeOff,
} from 'lucide-react';

/**
 * Gerador de QR Code para links e redes Wi-Fi (Marketing).
 * Tudo roda no navegador — nada é salvo no backend.
 * Permite logo no centro, legenda, cores e download em PNG (com fundo
 * transparente opcional) ou JPG.
 */

const EXPORT_SIZE = 1024; // resolução real do canvas; a prévia é só escala CSS
const INBASE_AZUL = '#1B3F5E';
const LOGO_INBASE = '/logo-inbase.png';

function slugify(str) {
  try {
    const u = new URL(str);
    return (u.hostname.replace(/^www\./, '') + u.pathname)
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/(^-|-$)+/g, '')
      .toLowerCase()
      .slice(0, 40) || 'qrcode';
  } catch {
    return 'qrcode';
  }
}

function slugifyTexto(str) {
  return String(str)
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/(^-|-$)+/g, '')
    .toLowerCase()
    .slice(0, 40);
}

/** Escapa caracteres reservados do formato WIFI: ( \ ; , : " ). */
function escapeWifi(str) {
  return String(str).replace(/([\\;,:"])/g, '\\$1');
}

/**
 * Monta o payload padrão que celulares (iOS/Android) reconhecem para
 * conectar automaticamente numa rede Wi-Fi ao ler o QR.
 * Ex.: WIFI:T:WPA;S:MinhaRede;P:senha123;H:true;;
 */
function montarPayloadWifi({ ssid, senha, seguranca, oculta }) {
  const s = escapeWifi(ssid);
  let out = `WIFI:T:${seguranca};S:${s};`;
  if (seguranca !== 'nopass') out += `P:${escapeWifi(senha)};`;
  if (oculta) out += 'H:true;';
  return out + ';';
}

export default function QRCodeGenerator() {
  const [modo, setModo] = useState('link'); // link | wifi

  const [url, setUrl] = useState('https://vendas.construtorainbase.com.br/');

  // Wi-Fi
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiSenha, setWifiSenha] = useState('');
  const [wifiSeguranca, setWifiSeguranca] = useState('WPA'); // WPA | WEP | nopass
  const [wifiOculta, setWifiOculta] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [fgColor, setFgColor] = useState(INBASE_AZUL);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [transparente, setTransparente] = useState(false);
  const [margem, setMargem] = useState(true);

  const [logoTipo, setLogoTipo] = useState('nenhum'); // nenhum | inbase | upload
  const [logoUpload, setLogoUpload] = useState(null); // dataURL
  const [logoTamanho, setLogoTamanho] = useState(22); // % do QR

  const [legenda, setLegenda] = useState('');
  const [legendaPos, setLegendaPos] = useState('abaixo'); // abaixo | acima

  const [nivel, setNivel] = useState('auto'); // auto | L | M | Q | H
  const [copiado, setCopiado] = useState(false);

  const previewRef = useRef(null); // wrapper: o <canvas> real do qrcode.react fica dentro
  const fileInputRef = useRef(null);

  const logoSrc = logoTipo === 'inbase' ? LOGO_INBASE : logoTipo === 'upload' ? logoUpload : null;

  // Pré-carrega o logo pra aquecer o cache do navegador antes do download
  useEffect(() => {
    if (!logoSrc) return;
    const img = new Image();
    img.src = logoSrc;
  }, [logoSrc]);

  const nivelEfetivo = useMemo(() => {
    if (nivel !== 'auto') return nivel;
    return logoSrc ? 'H' : 'M'; // com logo precisa de correção alta
  }, [nivel, logoSrc]);

  const imageSettings = useMemo(() => {
    if (!logoSrc) return undefined;
    const dim = Math.round((EXPORT_SIZE * logoTamanho) / 100);
    return { src: logoSrc, height: dim, width: dim, excavate: true };
  }, [logoSrc, logoTamanho]);

  const urlValida = useMemo(() => {
    if (!url.trim()) return false;
    try { new URL(url.trim()); return true; } catch { return false; }
  }, [url]);

  const wifiValido = useMemo(() => {
    if (!wifiSsid.trim()) return false;
    if (wifiSeguranca !== 'nopass' && !wifiSenha) return false;
    return true;
  }, [wifiSsid, wifiSenha, wifiSeguranca]);

  // Conteúdo efetivo que vira o QR, conforme o modo escolhido
  const qrValue = useMemo(() => {
    if (modo === 'wifi') {
      return montarPayloadWifi({
        ssid: wifiSsid,
        senha: wifiSenha,
        seguranca: wifiSeguranca,
        oculta: wifiOculta,
      });
    }
    return url.trim();
  }, [modo, url, wifiSsid, wifiSenha, wifiSeguranca, wifiOculta]);

  const conteudoValido = modo === 'wifi' ? wifiValido : urlValida;

  const nomeArquivo = modo === 'wifi'
    ? `wifi-${slugifyTexto(wifiSsid) || 'rede'}`
    : `qrcode-${slugify(url)}`;

  function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoUpload(reader.result);
      setLogoTipo('upload');
    };
    reader.readAsDataURL(file);
  }

  /** Monta um canvas final com margem + legenda opcional. */
  const montarCanvasFinal = useCallback((forJpg) => {
    const qr = previewRef.current?.querySelector('canvas');
    if (!qr) return null;

    const pad = margem ? Math.round(EXPORT_SIZE * 0.06) : Math.round(EXPORT_SIZE * 0.02);
    const temLegenda = legenda.trim().length > 0;
    const barraLegenda = temLegenda ? Math.round(EXPORT_SIZE * 0.14) : 0;

    const w = EXPORT_SIZE + pad * 2;
    const h = EXPORT_SIZE + pad * 2 + barraLegenda;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');

    // Fundo: branco no JPG (não suporta transparência); senão respeita a opção
    if (forJpg) {
      ctx.fillStyle = transparente ? '#FFFFFF' : bgColor;
      ctx.fillRect(0, 0, w, h);
    } else if (!transparente) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, w, h);
    }

    const qrY = temLegenda && legendaPos === 'acima' ? pad + barraLegenda : pad;
    ctx.drawImage(qr, pad, qrY, EXPORT_SIZE, EXPORT_SIZE);

    if (temLegenda) {
      ctx.fillStyle = fgColor;
      ctx.font = `700 ${Math.round(EXPORT_SIZE * 0.075)}px Arial, Helvetica, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const cy = legendaPos === 'acima'
        ? pad + barraLegenda / 2
        : pad + EXPORT_SIZE + pad + barraLegenda / 2;
      ctx.fillText(legenda.trim().toUpperCase(), w / 2, cy);
    }

    return out;
  }, [margem, legenda, legendaPos, transparente, bgColor, fgColor]);

  function baixar(formato) {
    const out = montarCanvasFinal(formato === 'jpg');
    if (!out) return;
    const mime = formato === 'jpg' ? 'image/jpeg' : 'image/png';
    const ext = formato === 'jpg' ? 'jpg' : 'png';
    const dataUrl = out.toDataURL(mime, 0.95);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${nomeArquivo}.${ext}`;
    a.click();
  }

  async function copiar() {
    const out = montarCanvasFinal(false);
    if (!out) return;
    try {
      await new Promise((resolve, reject) => {
        out.toBlob(async (blob) => {
          if (!blob) return reject();
          try {
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
            resolve();
          } catch (err) { reject(err); }
        }, 'image/png');
      });
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* navegador sem suporte a copiar imagem — ignora */
    }
  }

  const previewBg = transparente
    ? 'repeating-conic-gradient(#e5e7eb 0% 25%, #f9fafb 0% 50%) 50% / 20px 20px'
    : bgColor;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <QrCode size={24} className="text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gerador de QR Code</h1>
          <p className="text-sm text-gray-500">
            Transforme um link ou uma rede Wi-Fi em QR code personalizado para apresentações, materiais e campanhas
          </p>
        </div>
      </div>

      {/* Modo: Link x Wi-Fi */}
      <div className="flex gap-2">
        {[
          { v: 'link', label: 'Link', icon: Link2 },
          { v: 'wifi', label: 'Rede Wi-Fi', icon: Wifi },
        ].map(opt => {
          const Icone = opt.icon;
          return (
            <button
              key={opt.v}
              onClick={() => setModo(opt.v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                modo === opt.v
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icone size={16} /> {opt.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* ----- Controles ----- */}
        <div className="space-y-5">
          {/* Link */}
          {modo === 'link' && (
            <div className="bg-white rounded-xl shadow p-5">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Link2 size={16} className="text-blue-600" /> Link de destino
              </label>
              <input
                type="url"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              {!urlValida && url.trim() && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                  <AlertCircle size={13} /> Informe uma URL completa, incluindo https://
                </p>
              )}
            </div>
          )}

          {/* Wi-Fi */}
          {modo === 'wifi' && (
            <div className="bg-white rounded-xl shadow p-5 space-y-4">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Wifi size={16} className="text-blue-600" /> Rede Wi-Fi
              </h2>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Nome da rede (SSID)</label>
                <input
                  type="text"
                  value={wifiSsid}
                  onChange={e => setWifiSsid(e.target.value)}
                  placeholder="Ex.: Inbase-Visitantes"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Segurança</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: 'WPA', label: 'WPA/WPA2/WPA3' },
                    { v: 'WEP', label: 'WEP' },
                    { v: 'nopass', label: 'Sem senha (aberta)' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setWifiSeguranca(opt.v)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        wifiSeguranca === opt.v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {wifiSeguranca !== 'nopass' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Senha</label>
                  <div className="relative">
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      value={wifiSenha}
                      onChange={e => setWifiSenha(e.target.value)}
                      placeholder="Senha da rede"
                      autoComplete="off"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha(v => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wifiOculta}
                  onChange={e => setWifiOculta(e.target.checked)}
                  className="rounded"
                />
                Rede oculta (não aparece na lista de redes)
              </label>

              {!wifiValido && (wifiSsid.trim() || wifiSenha) && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600">
                  <AlertCircle size={13} />
                  {!wifiSsid.trim() ? 'Informe o nome da rede.' : 'Informe a senha da rede.'}
                </p>
              )}
              <p className="text-xs text-gray-400">
                Ao apontar a câmera, o celular oferece conectar automaticamente. Nada é enviado ao servidor.
              </p>
            </div>
          )}

          {/* Cores */}
          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Palette size={16} className="text-blue-600" /> Cores
            </h2>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="color" value={fgColor} onChange={e => setFgColor(e.target.value)}
                  className="w-9 h-9 rounded border border-gray-200 cursor-pointer" />
                QR
              </label>
              <label className={`flex items-center gap-2 text-sm text-gray-600 ${transparente ? 'opacity-40' : ''}`}>
                <input type="color" value={bgColor} disabled={transparente}
                  onChange={e => setBgColor(e.target.value)}
                  className="w-9 h-9 rounded border border-gray-200 cursor-pointer" />
                Fundo
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={transparente}
                  onChange={e => setTransparente(e.target.checked)} className="rounded" />
                Fundo transparente (PNG)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={margem}
                  onChange={e => setMargem(e.target.checked)} className="rounded" />
                Margem de segurança
              </label>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <ImageIcon size={16} className="text-blue-600" /> Logo no centro
            </h2>
            <div className="flex flex-wrap gap-2">
              {[
                { v: 'nenhum', label: 'Nenhum' },
                { v: 'inbase', label: 'Logo Inbase' },
                { v: 'upload', label: 'Enviar imagem' },
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => {
                    if (opt.v === 'upload') { fileInputRef.current?.click(); }
                    else setLogoTipo(opt.v);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    logoTipo === opt.v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </div>

            {logoTipo === 'upload' && logoUpload && (
              <div className="flex items-center gap-3">
                <img src={logoUpload} alt="logo" className="w-10 h-10 object-contain border border-gray-200 rounded bg-white" />
                <button
                  onClick={() => { setLogoUpload(null); setLogoTipo('nenhum'); }}
                  className="flex items-center gap-1 text-xs text-red-600 hover:underline"
                >
                  <Trash2 size={13} /> Remover
                </button>
              </div>
            )}

            {logoSrc && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tamanho do logo: {logoTamanho}%</label>
                <input
                  type="range" min={12} max={30} value={logoTamanho}
                  onChange={e => setLogoTamanho(Number(e.target.value))}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Com logo, a correção de erro sobe automaticamente para “Alta” pra manter a leitura.
                </p>
              </div>
            )}
          </div>

          {/* Legenda */}
          <div className="bg-white rounded-xl shadow p-5 space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Type size={16} className="text-blue-600" /> Texto / legenda (opcional)
            </h2>
            <input
              type="text"
              value={legenda}
              maxLength={40}
              onChange={e => setLegenda(e.target.value)}
              placeholder='Ex.: "Aponte a câmera" ou "Acesse o sistema de vendas"'
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {legenda.trim() && (
              <div className="flex gap-2">
                {['abaixo', 'acima'].map(p => (
                  <button
                    key={p}
                    onClick={() => setLegendaPos(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border capitalize transition-colors ${
                      legendaPos === p
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {p} do QR
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Correção de erro */}
          <div className="bg-white rounded-xl shadow p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Correção de erro</label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: 'auto', label: 'Automática' },
                { v: 'L', label: 'Baixa (7%)' },
                { v: 'M', label: 'Média (15%)' },
                { v: 'Q', label: 'Alta (25%)' },
                { v: 'H', label: 'Máxima (30%)' },
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setNivel(opt.v)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    nivel === opt.v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Quanto maior a correção, mais o QR resiste a ruído/logo — porém fica mais denso.
              Usando: <strong>{nivelEfetivo}</strong>
            </p>
          </div>
        </div>

        {/* ----- Prévia + download ----- */}
        <div className="lg:sticky lg:top-6 self-start space-y-4">
          <div className="bg-white rounded-xl shadow p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Prévia</p>
            <div
              className="rounded-xl p-4 flex flex-col items-center justify-center"
              style={{ background: previewBg }}
            >
              {conteudoValido ? (
                <>
                  {legenda.trim() && legendaPos === 'acima' && (
                    <div className="text-center font-bold uppercase text-sm mb-2 break-words max-w-full"
                      style={{ color: fgColor }}>
                      {legenda.trim()}
                    </div>
                  )}
                  <div ref={previewRef}>
                    <QRCodeCanvas
                      value={qrValue}
                      size={EXPORT_SIZE}
                      level={nivelEfetivo}
                      includeMargin={margem}
                      bgColor={transparente ? 'transparent' : bgColor}
                      fgColor={fgColor}
                      imageSettings={imageSettings}
                      style={{ width: 260, height: 260, maxWidth: '100%', display: 'block' }}
                    />
                  </div>
                  {legenda.trim() && legendaPos === 'abaixo' && (
                    <div className="text-center font-bold uppercase text-sm mt-2 break-words max-w-full"
                      style={{ color: fgColor }}>
                      {legenda.trim()}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-[260px] h-[260px] flex items-center justify-center text-center text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg px-4">
                  {modo === 'wifi'
                    ? 'Preencha o nome da rede (e a senha) para gerar o QR code'
                    : 'Informe um link válido para gerar o QR code'}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-5 space-y-2">
            <button
              onClick={() => baixar('png')}
              disabled={!conteudoValido}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Download size={16} /> Baixar PNG{transparente ? ' (transparente)' : ''}
            </button>
            <button
              onClick={() => baixar('jpg')}
              disabled={!conteudoValido}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-semibold hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              <Download size={16} /> Baixar JPG
            </button>
            <button
              onClick={copiar}
              disabled={!conteudoValido}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {copiado ? <><Check size={16} className="text-green-600" /> Copiado!</> : <><Copy size={16} /> Copiar imagem</>}
            </button>
            {transparente && (
              <p className="text-xs text-gray-400 pt-1">
                JPG não suporta transparência — o arquivo JPG sai com fundo branco.
              </p>
            )}
            <p className="text-xs text-gray-400">
              Imagem exportada em {EXPORT_SIZE}px — boa para impressão e projeção.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
